import "./ActiveFilters.scss";

import React from "react";

import withTranslations from "../../../../../../hocs/withTranslations";
import ApiHelper from "../../../../../../lib/UniversalTable/CoreHelpers/ApiHelper";
import FilterOptionsHelper from "../../../../../../lib/UniversalTable/CoreHelpers/FilterOptionsHelper";
import normalizeFilterValues from "../../../../../../lib/UniversalTable/CoreHelpers/normalizeFilterValues";
import {
  formatRangeFilterDisplayLabel,
  hasActiveRangeFilterBounds
} from "../../../../../../lib/UniversalTable/CoreHelpers/rangeFilterHelpers";
import {
  getVisibleActiveFilterKeys,
  isSettingLocked
} from "../../../../../../lib/UniversalTable/lockedSettings";
import UniversalTableContext from "../../../../../../lib/UniversalTable/UniversalTableContext";
import Renderer from "./renderers";

@withTranslations
class ActiveFilters extends React.Component {
  static contextType = UniversalTableContext;

  getRulesetFilterLabel(columnData, selectedValue) {
    if (!columnData || !ApiHelper.getIsFilterableByRuleset(columnData)) {
      return null;
    }

    return Object.prototype.hasOwnProperty.call(
      columnData.filter_data,
      selectedValue
    )
      ? selectedValue
      : null;
  }

  renderSearchFilter(searchField, searchValue, isLocked) {
    const columnData = ApiHelper.findColumnForFiltering(
      this.context,
      searchField
    );

    if (!columnData) {
      return null;
    }

    return (
      <div
        className={`active-filter${isLocked ? " locked" : ""}`}
        key={`active-filter-${searchField}`}
        data-test-id="universal-table-active-filter"
        data-test-filter-key={searchField}
        data-test-locked={isLocked ? "true" : "false"}
      >
        <span className="field">{columnData.label}:</span>
        <span className="value">{searchValue}</span>
        {isLocked ? null : (
          <span
            className="remove-filter"
            data-test-id="universal-table-active-filter-remove"
            onClick={() => {
              this.removeSearch(searchField);
            }}
          >
            <i className="fal fa-times" />
          </span>
        )}
      </div>
    );
  }

  renderFilter(filterField, filterValue, isLocked) {
    const { translate } = this.props;

    const columnData = ApiHelper.findColumnForFiltering(
      this.context,
      filterField
    );

    if (!columnData) {
      return null;
    }

    let valueLabel = "";

    if (
      typeof filterValue.min !== "undefined" ||
      typeof filterValue.max !== "undefined"
    ) {
      if (!hasActiveRangeFilterBounds(filterValue, {
        isDateRange: ApiHelper.getIsDateRange(columnData),
        column: columnData
      })) {
        return null;
      }

      valueLabel = formatRangeFilterDisplayLabel({
        filterValue,
        columnData,
        translate
      });
    } else {
      const normalizedFilterValues = normalizeFilterValues(filterValue);

      if (normalizedFilterValues.length) {
        valueLabel = normalizedFilterValues
          .map(value => {
            const rulesetLabel = this.getRulesetFilterLabel(columnData, value);
            const optionValue = rulesetLabel || value;

            return translate(
              FilterOptionsHelper.getOptionTranslationKey(
                columnData,
                optionValue
              )
            );
          })
          .join(", ");
      }
    }

    return (
      <div
        className={`active-filter${isLocked ? " locked" : ""}`}
        key={`active-filter-${filterField}`}
        data-test-id="universal-table-active-filter"
        data-test-filter-key={filterField}
        data-test-locked={isLocked ? "true" : "false"}
      >
        <span className="field">{columnData.label}:</span>
        <span className="value">
          <Renderer value={valueLabel} column={columnData} />
        </span>
        {isLocked ? null : (
          <span
            className="remove-filter"
            data-test-id="universal-table-active-filter-remove"
            onClick={() => {
              this.removeFilter(filterField);
            }}
          >
            <i className="fal fa-times" />
          </span>
        )}
      </div>
    );
  }

  removeSearch(searchField) {
    const { search, setSearch, getLockedSettings } = this.context;

    if (isSettingLocked(getLockedSettings(), searchField)) {
      return;
    }

    delete search[searchField];

    setSearch(search);
  }

  removeFilter(filterField) {
    const { filters, setFilters, getLockedSettings } = this.context;

    if (isSettingLocked(getLockedSettings(), filterField)) {
      return;
    }

    delete filters[filterField];

    setFilters(filters);
  }

  render() {
    const { search, filters, getExcludedSettings, getLockedSettings } =
      this.context;

    const excludedSettings = getExcludedSettings();
    const lockedSettings = getLockedSettings();

    const searchKeys = getVisibleActiveFilterKeys(
      Object.keys(search),
      excludedSettings,
      lockedSettings
    );

    const filtersKeys = getVisibleActiveFilterKeys(
      Object.keys(filters),
      excludedSettings,
      lockedSettings
    );

    if (!searchKeys.length && !filtersKeys.length) {
      return null;
    }

    return (
      <div className="active-filters" data-test-id="universal-table-active-filters">
        {searchKeys.map(key =>
          this.renderSearchFilter(
            key,
            search[key],
            isSettingLocked(lockedSettings, key)
          )
        )}
        {filtersKeys.map(key =>
          this.renderFilter(
            key,
            filters[key],
            isSettingLocked(lockedSettings, key)
          )
        )}
      </div>
    );
  }
}

export default ActiveFilters;
